import React from 'react';
import { type useTransactionValues } from '@/hooks/use-transaction-values';
import { Divider, Tooltip } from '@mui/material';
import LabelValue from 'components/LabelValue';
import TransactionChart from 'components/TransactionChart';
import { useAppSelector } from 'features';
import { getDesyncedChargingConnectorIdsSelector } from 'features/Connectors';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { ConnectorStatusEnum } from 'types/swagger';

export type TransactionValuesProps = Partial<ReturnType<typeof useTransactionValues>>;

const TransactionValues: React.FC<TransactionValuesProps> = ({
  batterySoc,
  autonomy,
  donutData,
  batteryChargePercentage,
  capacity,
  power,
  chargedEnergy,
  startTime,
  endTime,
  status,
  transaction,
  estimatedEndTime,
  remainingTime,
}) => {
  const desyncedChargingConnectorIds = useAppSelector(getDesyncedChargingConnectorIdsSelector);
  const connectorHasDesync =
    transaction && desyncedChargingConnectorIds.includes(transaction.connector);

  const userStart = transaction?.user_start
    ? transaction?.user_start?.email
    : transaction?.anonymous_transaction_request?.email;

  const userStop =
    transaction?.timestamp_stop && transaction?.user_stop
      ? transaction?.user_stop?.email
      : transaction?.anonymous_transaction_request?.email;

  const registeredUser = transaction?.anonymous_transaction_request ? 'No' : 'Sí';

  return connectorHasDesync && transaction?.timestamp_stop ? null : (
    <div className="mb-2">
      {batterySoc || autonomy ? (
        <div data-test-id="connector-drawer-vehicle-details">
          <Divider className="my-3" />
          <h5>Vehículo</h5>
        </div>
      ) : null}
      {batterySoc ? (
        <LabelValue
          testID="connector-drawer-battery-soc"
          label="Estado de carga:"
          value={
            <div className="d-flex align-items-center justify-content-center">
              <div
                style={{
                  height: '48px',
                  width: '48px',
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      innerRadius={8}
                      outerRadius={15}
                      startAngle={90}
                      endAngle={-270}
                      stroke="none"
                    >
                      {donutData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <Tooltip
                classes={{
                  tooltip: 'widget-info-tooltip',
                }}
                title={<span>{batteryChargePercentage}</span>}
                placement="top"
                disableFocusListener={!capacity}
                disableHoverListener={!capacity}
                disableInteractive={!capacity}
                disableTouchListener={!capacity}
              >
                <b>{batterySoc}</b>
              </Tooltip>
            </div>
          }
        />
      ) : null}
      {autonomy ? <LabelValue label="Autonomía:" value={autonomy} /> : null}
      <Divider className="my-3" />
      <h5>Transacción</h5>
      <LabelValue
        testID="connector-drawer-transaction-id"
        label="ID de transacción:"
        value={transaction ? transaction?.id : '- - -'}
      />
      {status === ConnectorStatusEnum.Charging ? (
        <LabelValue
          testID="connector-drawer-charging-power"
          label="Potencia de carga:"
          value={power || '- - -'}
        />
      ) : null}
      <LabelValue
        testID="connector-drawer-charged-energy"
        label="Energía cargada:"
        value={chargedEnergy}
      />
      <LabelValue
        testID="connector-drawer-start-time"
        label="Inicio:"
        value={startTime || '- - -'}
      />
      <LabelValue
        testID="connector-drawer-user-start"
        label="Iniciada por:"
        value={userStart || '---'}
      />
      <LabelValue
        testID="connector-drawer-registered-user"
        label="Usuario registrado:"
        value={registeredUser}
      />
      {status === ConnectorStatusEnum.Finishing ? (
        <>
          <LabelValue
            testID="connector-drawer-end-time"
            label="Término:"
            value={endTime || '- - -'}
          />
          <LabelValue
            testID="connector-drawer-user-end"
            label="Detenida por:"
            value={userStop || '- - -'}
          />
        </>
      ) : null}
      {!endTime && estimatedEndTime ? (
        <LabelValue label="Término est.:" value={estimatedEndTime} />
      ) : null}
      {!endTime && remainingTime ? (
        <LabelValue label="Tiempo restante:" value={remainingTime} />
      ) : null}
      {transaction ? <TransactionChart transaction={transaction} /> : null}
    </div>
  );
};

export default TransactionValues;
